import { useReducer, useEffect } from 'react';

// Cache to avoid re-probing the same URL multiple times
const validationCache = new Map<string, boolean>();
const loadingUrls = new Set<string>();

export const clearValidationCache = () => {
  validationCache.clear();
  loadingUrls.clear();
};

/**
 * Probes an image URL before passing it to a component.
 * Returns the URL if it loads successfully, or an empty string on error.
 * This allows components like bds-avatar to fall back to initials when
 * the thumbnail URL is broken, without modifying the web component itself.
 */
export const useValidatedThumbnail = (url?: string): string => {
  const [, forceUpdate] = useReducer((x: number) => x + 1, 0);

  useEffect(() => {
    if (!url || validationCache.has(url)) return;

    if (loadingUrls.has(url)) return;

    loadingUrls.add(url);

    const img = new Image();
    img.onload = () => {
      validationCache.set(url, true);
      loadingUrls.delete(url);
      forceUpdate();
    };
    img.onerror = () => {
      validationCache.set(url, false);
      loadingUrls.delete(url);
      forceUpdate();
    };
    img.src = url;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [url]);

  if (!url) return '';
  return validationCache.get(url) ? url : '';
};
